import { useEffect, useRef } from 'react';

type CollapseState = 'open' | 'collapsed';

interface UseMultiScrollDirectionProps {
  scrollContainerRefs: React.RefObject<HTMLElement>[];
  threshold?: number;
  initialState?: CollapseState;
  onStateChange?: (state: CollapseState) => void;
}

/**
 * Reports 'collapsed' when any container scrolls down and 'open' once it is back near the top.
 * Passive, rAF-throttled listeners that keep everything in refs, so scrolling never renders the
 * caller — only a state flip reaches `onStateChange`. Listeners bind when the container count
 * changes, so pass `[]` until the containers are mounted.
 */
const useMultiScrollDirection = ({
  scrollContainerRefs,
  threshold = 50,
  initialState = 'open',
  onStateChange
}: UseMultiScrollDirectionProps) => {
  const stateRef = useRef<CollapseState>(initialState);
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;

  useEffect(() => {
    const setState = (next: CollapseState) => {
      if (next === stateRef.current) return;
      stateRef.current = next;
      onStateChangeRef.current?.(next);
    };

    const cleanups = scrollContainerRefs.map(ref => {
      const element = ref.current;
      if (!element) return () => {};
      let lastTop = element.scrollTop;
      let frame = 0;

      const handleScroll = () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          const currentTop = element.scrollTop;
          if (Math.abs(currentTop - lastTop) < threshold) return;
          const isDown = currentTop > lastTop;
          lastTop = currentTop;

          const isShortContent = element.scrollHeight <= element.clientHeight;
          const isAtBottom = currentTop + element.clientHeight >= element.scrollHeight - 10;
          if (isShortContent || currentTop <= threshold) setState('open');
          else if (isDown && !isAtBottom) setState('collapsed');
        });
      };
      // Re-open on an upward wheel gesture over a column that can't scroll up. Wheel events arrive every
      // frame, so layout is only read when the gesture could actually flip the state.
      const handleWheel = (event: WheelEvent) => {
        if (event.deltaY < 0 && stateRef.current === 'collapsed' && element.scrollTop <= threshold) setState('open');
      };

      element.addEventListener('scroll', handleScroll, { passive: true });
      element.addEventListener('wheel', handleWheel, { passive: true });
      return () => {
        cancelAnimationFrame(frame);
        element.removeEventListener('scroll', handleScroll);
        element.removeEventListener('wheel', handleWheel);
      };
    });

    return () => cleanups.forEach(cleanup => cleanup());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollContainerRefs.length, threshold]);
};

export default useMultiScrollDirection;
